import api from "./api";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL || "";

// Auth service for login/logout using HttpOnly cookies
// POST /auth/login -> { message, user } (token is set as HttpOnly cookie)
export async function login(email, password) {
  if (!email || !password) throw new Error("Email and password are required");
  const url = `${API_BASE_URL}/auth/login`;
  const body = {
    email,
    password,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let message = "Login failed";
    let retryAfter = null;

    try {
      const retryAfterHeader = response.headers.get("Retry-After");
      if (retryAfterHeader) {
        const parsedRetryAfter = Number(retryAfterHeader);
        if (Number.isFinite(parsedRetryAfter) && parsedRetryAfter > 0) {
          retryAfter = parsedRetryAfter;
        }
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const errJson = await response.json();
        if (errJson && typeof errJson === "object") {
          message = errJson.message || errJson.error || message;
          if (retryAfter == null) {
            const jsonRetryAfter = Number(errJson.retryAfter);
            if (Number.isFinite(jsonRetryAfter) && jsonRetryAfter > 0) {
              retryAfter = jsonRetryAfter;
            }
          }
        }
      } else {
        const errText = await response.text();
        if (errText) message = errText;
      }
    } catch {
      // keep fallback message
    }

    const error = new Error(message || "Login failed");
    error.status = response.status;
    error.retryAfter = retryAfter;
    throw error;
  }

  const res = await response.json();
  if (!res || !res.user) {
    throw new Error("Invalid login response");
  }
  return res; // { message, user }
}

// POST /auth/sso-token - creates a short-lived, single-use token for 3D SSO
export async function createSsoToken() {
  const response = await api.request("/auth/sso-token", {
    method: "POST",
    body: JSON.stringify({}),
    suppressErrorPage: true,
  });
  const token = typeof response?.token === "string" ? response.token.trim() : "";

  if (!token) {
    throw new Error("The server did not return an SSO token");
  }

  return token;
}

// POST /auth/logout - clears the HttpOnly cookie
export async function logout() {
  try {
    await api.request("/auth/logout", {
      method: "POST",
      body: JSON.stringify({}),
      suppressErrorPage: true,
    });
  } catch (err) {
    // Even if logout fails on server, we should still clear local state
    console.warn("Logout request failed:", err);
  }
}

// GET /auth/me - verify current session and get user info
export async function getCurrentUser() {
  const url = `${API_BASE_URL}/auth/me`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
    credentials: "include", // Include cookies for HttpOnly authentication
  });

  if (!response.ok) {
    if (response.status === 401) {
      return null; // Not authenticated
    }
    throw new Error("Failed to get current user");
  }

  return await response.json();
}
